import React from "react";
import * as ExcelJS from "exceljs";
import { Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { saveAs } from "file-saver";

const ExportECAButton = ({ data, participantes }) => {
  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();

    const applyHeaderStyle = (worksheet, color) => {
      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: color },
        };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: worksheet.columnCount },
      };
    };

    const applyDataStyle = (worksheet) => {
      worksheet.eachRow((row, rowNumber) => {
        row.height = 20;
        row.eachCell((cell) => {
          cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });
    };

    const marcarTematicas = (tipo_estado) => {
      const tipos = Array.isArray(tipo_estado)
        ? tipo_estado.map(String)
        : typeof tipo_estado === "string"
        ? tipo_estado.split(",").map((t) => t.trim())
        : [];
      return {
        tecnica: tipos.includes("1") ? "X" : "",
        ambiental: tipos.includes("2") ? "X" : "",
        organizacional: tipos.includes("3") ? "X" : "",
        enfoques: tipos.includes("6") ? "X" : "",
        dhha: tipos.includes("5") ? "X" : "",
      };
    };

    // 🔹 Hoja 1: ECAs
    const worksheet1 = workbook.addWorksheet("ECAs");
    worksheet1.columns = [
      { header: "#", key: "index", width: 5 }, // 📌 Nueva columna #
      { header: "Departamento", key: "departamento", width: 20 },
      { header: "Municipio", key: "municipio", width: 20 },
      { header: "Nombre Sesión Eca", key: "nombre_sesion", width: 30 },
      { header: "Fecha", key: "fecha", width: 15 },
      { header: "Conformación", key: "conformacion", width: 60 }, // 📌 Conformación
      { header: "Número de participantes", key: "participantes", width: 10 },
      { header: "Técnica", key: "tecnica", width: 10 },
      { header: "Ambiental", key: "ambiental", width: 10 },
      { header: "Organizacional", key: "organizacional", width: 15 },
      { header: "Enfoques", key: "enfoques", width: 10 },
      { header: "DHHA", key: "dhha", width: 10 },
    ];

    applyHeaderStyle(worksheet1, "FF007ACC"); // Azul encabezado

    data.forEach((item, idx) => {
      const tematicas = marcarTematicas(item.tipo_estado);
      worksheet1.addRow({
        index: idx + 1, // 📌 Autonumérico
        departamento: item.nombre_departamento,
        municipio: item.nombre_municipio,
        nombre_sesion: item.nombre,
        fecha: new Date(item.fechaCreacion).toLocaleDateString(),
        conformacion: item.conformaciones || "", // 📌 Conformación
        participantes: item.participantes,
        tecnica: tematicas.tecnica,
        ambiental: tematicas.ambiental,
        organizacional: tematicas.organizacional,
        enfoques: tematicas.enfoques,
        dhha: tematicas.dhha,
      });
    });

    applyDataStyle(worksheet1);

    // 🔹 Hoja 2: Participantes
    const worksheet2 = workbook.addWorksheet("Participantes");
    worksheet2.columns = [
      { header: "#", key: "index", width: 5 }, // 📌 Nueva columna #
      { header: "Departamento", key: "departamento", width: 20 },
      { header: "Municipio", key: "municipio", width: 20 },
      { header: "Nombre Sesión Eca", key: "nombre_sesion", width: 30 },
      { header: "Fecha", key: "fecha", width: 15 },
      { header: "Participante", key: "participante", width: 30 },
      { header: "Identificación", key: "identificacion", width: 20 },
      { header: "Teléfono", key: "telefono", width: 15 },
      { header: "Conformación", key: "conformacion", width: 40 }, 
      { header: "Técnica", key: "tecnica", width: 10 },
      { header: "Ambiental", key: "ambiental", width: 10 },
      { header: "Organizacional", key: "organizacional", width: 15 },
      { header: "Enfoques", key: "enfoques", width: 10 },
      { header: "DHHA", key: "dhha", width: 10 },
    ];

    applyHeaderStyle(worksheet2, "FF28A745"); // Verde encabezado

    participantes.forEach((item, idx) => {
      const tematicas = marcarTematicas(item.tipo_estado);
      worksheet2.addRow({
        index: idx + 1, // 📌 Autonumérico
        departamento: item.nombre_departamento,
        municipio: item.nombre_municipio,
        nombre_sesion: item.nombreECA,
        fecha: new Date(item.fechaCreacion).toLocaleDateString(),
        participante: item.nombre_participante,
        identificacion: item.numero_identificacion,
        telefono: item.numero_telefonico_1 ?? "N/A",
        conformacion: item.conformaciones || "", // 📌 Conformación
        tecnica: tematicas.tecnica,
        ambiental: tematicas.ambiental,
        organizacional: tematicas.organizacional,
        enfoques: tematicas.enfoques,
        dhha: tematicas.dhha,
      });
    });

    applyDataStyle(worksheet2);

    // 🔥 Guardar archivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(blob, "ECAs_Detalle.xlsx");
  };

  return (
    <Button
      type="default"
      icon={<DownloadOutlined />}
      onClick={exportToExcel}
    >
      Exportar Excel
    </Button>
  );
};

export default ExportECAButton;
